import { useEffect, useMemo, useState } from 'react'
import { avisoDeCoordenadasGrandes } from '@viewer/lib/georef/coordenadasGrandes'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { loadModelInfo, formatCoord, type ModelInfo } from '@viewer/lib/modelInfo'
import { schemaLabel } from '@viewer/lib/ifcSchema'
import {
  extractSpatialOrigins,
  spatialTypeLabel,
  type SpatialOrigin,
  type SpatialOriginsResult,
} from '@viewer/lib/spatialPlacements'
import { applyOriginMarkerOverlay, irParaOrigem } from '@viewer/lib/originMarker'
import { rawIfc } from '@viewer/lib/rawIfc'
import { writeProductPlacement } from '@viewer/lib/sitePlacementWrite'
import { downloadBlob } from '@core/lib/downloadBlob'
import {
  buildPropertyDictionary,
  type PropertyDictionary,
} from '@viewer/lib/propertyDictionary'
import { detectTrueNorthDeg } from '@viewer/lib/trueNorth'
import { checkMapConversionScale, placementDeFragments } from '@viewer/lib/geoTransform'
import Section from './Section'
import UnidadesDoModelo from './UnidadesDoModelo'
import LeituraDoMapaView, { SitioView } from './LeituraDoMapaView'
import VerificacoesGeorrefView from './VerificacoesGeorrefView'
import { verificacoesGuardadas } from '@viewer/lib/georef/verificacoesGeorref'
import { leituraGuardada, sitioGuardado } from '@viewer/lib/georef/leituraDoMapa'

// Contador de módulo para ids estáveis das regras de filtro pré-
// preenchidas (evita Math.random() no corpo do componente, que o lint
// sinaliza como função impura durante o render).
let filterRuleSeq = 0

function ModelInfoPanel() {
  const activeModelId = useAppStore((s) => s.activeModelId)
  const distanciaGrande = useAppStore((s) => (s.activeModelId ? s.coordenadasGrandes[s.activeModelId] : undefined))
  const models = useAppStore((s) => s.models)
  const setFilterPanelOpen = useAppStore((s) => s.setFilterPanelOpen)
  const setFilterPrefill = useAppStore((s) => s.setFilterPrefill)
  const origemEmFoco = useAppStore((s) => s.originMarkerFocus)
  const setOriginMarkerFocus = useAppStore((s) => s.setOriginMarkerFocus)
  const activeMeta = models.find((m) => m.id === activeModelId)
  const [info, setInfo] = useState<ModelInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [origins, setOrigins] = useState<SpatialOriginsResult | null>(null)
  const [originsBusy, setOriginsBusy] = useState(false)
  const [originsErr, setOriginsErr] = useState<string | null>(null)
  const [editNode, setEditNode] = useState<SpatialOrigin | null>(null)
  const [dict, setDict] = useState<PropertyDictionary | null>(null)
  const [dictBusy, setDictBusy] = useState(false)
  const [dictErr, setDictErr] = useState<string | null>(null)

  // Trocar de modelo invalida tudo o que se leu do anterior. Ajusta-se
  // DURANTE o render, com guarda pelo modelo já adoptado: é o padrão
  // que o React recomenda para isto e o mesmo que o painel de Filtro
  // usa. Fazê-lo no corpo do efeito encadeava renders e deixava um
  // render a mostrar as origens do modelo anterior.
  const [modeloAdoptado, setModeloAdoptado] = useState(activeModelId)
  if (activeModelId !== modeloAdoptado) {
    setModeloAdoptado(activeModelId)
    setInfo(null)
    setOrigins(null)
    setOriginsErr(null)
    setEditNode(null)
    setDict(null)
    setDictErr(null)
  }

  useEffect(() => {
    if (!activeModelId) return
    const m = viewerApi.getModel(activeModelId)
    if (!m) {
      const unsub = viewerApi.subscribe(() => {
        const ready = viewerApi.getModel(activeModelId)
        if (ready) {
          unsub()
          run(ready)
        }
      })
      return unsub
    }
    return run(m)

    function run(model: ReturnType<typeof viewerApi.getModel> & object) {
      let cancelled = false
      setLoading(true)
      loadModelInfo(model)
        .then((res) => {
          if (cancelled || activeModelId === null) return
          setInfo(res)
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
      return () => {
        cancelled = true
      }
    }
  }, [activeModelId])

  // TrueNorth declarado no IfcGeometricRepresentationContext, lido dos
  // bytes em cache (prefixo chega: o contexto vive no topo da secção de
  // dados). Meramente informativo por spec: NÃO roda a geometria.
  // ANTES do early-return: os hooks têm de correr em todas as renders
  // (rules-of-hooks), mesmo sem modelo ativo.
  const trueNorthDeg = useMemo(() => {
    if (!activeModelId) return null
    const bytes = rawIfc.getCachedBytes(activeModelId)
    if (!bytes) return null
    const text = new TextDecoder('latin1').decode(
      bytes.subarray(0, 2_000_000),
    )
    return detectTrueNorthDeg(text)
  }, [activeModelId])

  if (!activeModelId) return null

  const onComputeOrigins = () => {
    if (!activeModelId || originsBusy) return
    setOriginsBusy(true)
    setOriginsErr(null)
    void (async () => {
      try {
        const res = await extractSpatialOrigins(activeModelId)
        setOrigins(res)
        if (!res) setOriginsErr('Extração falhou (parser paralelo indisponível).')
        // Com o parser agora carregado, o marcador de origem 3D passa a
        // incluir os nós espaciais: refrescar se estiver ligado.
        if (useAppStore.getState().originMarkerOverlay) {
          void applyOriginMarkerOverlay(true)
        }
      } catch (e) {
        setOriginsErr(e instanceof Error ? e.message : 'Extração falhou.')
      } finally {
        setOriginsBusy(false)
      }
    })()
  }

  const onComputeDictionary = () => {
    if (!activeModelId || dictBusy) return
    setDictBusy(true)
    setDictErr(null)
    void (async () => {
      try {
        const res = await buildPropertyDictionary(activeModelId)
        setDict(res)
        if (!res) setDictErr('Leitura falhou (modelo não pronto).')
      } catch (e) {
        setDictErr(e instanceof Error ? e.message : 'Leitura falhou.')
      } finally {
        setDictBusy(false)
      }
    })()
  }

  // Clicar numa propriedade abre o Filtro (Shift+F) já apontado a
  // Pset.Propriedade, com o valor por escolher: o dropdown de valores do
  // painel enche-se com os valores distintos desta propriedade, e o
  // utilizador seleciona o que quer. Operador "=" (o default válido para
  // a dimensão propriedade; contains/startsWith não se aplicam aqui).
  const openFilterForProp = (psetName: string, propName: string) => {
    setFilterPrefill({
      combinator: 'and',
      rules: [
        {
          id: `prop-${++filterRuleSeq}`,
          dimension: 'property',
          propertyKey: `${psetName}.${propName}`,
          operator: 'equals',
          values: [],
        },
      ],
    })
    setFilterPanelOpen(true)
  }

  const crs = info?.crs ?? null
  const mc = crs?.mapConversion ?? null
  // O Scale do MapConversion tem de bater certo com a unidade do ficheiro.
  // Quando não bate (típico: ficheiro em mm com Scale=1), o mundo do viewer
  // deixa de estar em metros e o mapa/KMZ mentem em silêncio. Só avisamos:
  // corrigir por adivinhação seria pior.
  const leituraNova = activeModelId ? leituraGuardada(activeModelId) : null
  // Num .frag não há unidade do projecto: o Scale não se aplicou e não se
  // julga (ver placementDeFragments).
  const deFragments = activeMeta?.sourceFormat === 'frag'
  const escalaNaoAplicada = mc && deFragments ? placementDeFragments(mc).escalaIgnorada : null
  const scaleCheck = mc && !leituraNova && !deFragments
    ? checkMapConversionScale(mc.scale, origins?.factorToMetres ?? 1)
    : null
  const stats = info?.stats ?? null
  const hasTypes = stats ? stats.typeCount > 0 : null

  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between border-b border-[var(--color-border)]/40 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        title={
          open ? 'Esconder informações do modelo' : 'Mostrar informações do modelo'
        }
      >
        <span>Informações do modelo</span>
        <span className="flex items-center gap-2 text-[var(--color-text-dim)]">
          <span className="truncate font-normal normal-case tracking-normal text-[10px] max-w-[160px]">
            {activeMeta?.fileName ?? ''}
          </span>
          <span>{open ? '−' : '+'}</span>
        </span>
      </button>
      {!open ? null : (
        <>
      <Section title="Modelo" defaultOpen={false}>
        <div className="space-y-2 text-[11px]">
          <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
            <span className="text-[var(--color-text-muted)]">Ficheiro</span>
            <span className="truncate text-[var(--color-text)]">
              {activeMeta?.fileName ?? '-'}
            </span>
            <span className="text-[var(--color-text-muted)]">Schema</span>
            <span className="font-mono text-[var(--color-text)]">
              {schemaLabel(activeMeta?.schemaVersion ?? null)}
            </span>
            {stats && (
              <>
                <span className="text-[var(--color-text-muted)]">
                  Entidades
                </span>
                <span className="text-[var(--color-text)]">
                  {stats.total} (instâncias {stats.instanceCount}, types{' '}
                  {stats.typeCount})
                </span>
              </>
            )}
          </div>

          {stats && (
            <div
              className={`rounded border px-2 py-1.5 ${
                hasTypes
                  ? 'border-emerald-700/50 bg-[var(--color-ok-bg)] text-emerald-100'
                  : 'border-amber-700/50 bg-[var(--color-warn-bg)] text-[var(--color-warn-text)]'
              }`}
            >
              <div className="text-[10px] uppercase tracking-wide">
                {hasTypes ? 'Modelo com types IFC' : 'Modelo sem types IFC'}
              </div>
              <div className="mt-0.5 text-[11px]">
                {hasTypes
                  ? `${stats.typeCount} entidades IFC*TYPE encontradas. As instâncias podem partilhar propriedades via IfcRelDefinesByType.`
                  : 'Não foram encontradas categorias IFC*TYPE neste modelo. Todos os elementos vivem como instâncias e definem propriedades localmente. Isto é uma característica do IFC original; a conversão para Fragments preserva os types se existirem.'}
              </div>
            </div>
          )}

          {loading && !info && (
            <div className="italic text-[var(--color-text-muted)]">
              A ler informação do modelo…
            </div>
          )}
        </div>
      </Section>

      {activeModelId && (
        <Section title="Unidades" defaultOpen={false}>
          <UnidadesDoModelo modelId={activeModelId} />
        </Section>
      )}

      <Section title="IfcProject" defaultOpen={false}>
        {!info?.project ? (
          <div className="text-[11px] italic text-[var(--color-text-muted)]">
            {loading
              ? 'A ler IfcProject…'
              : 'IfcProject não encontrado neste modelo.'}
          </div>
        ) : (
          <div className="space-y-2 text-[11px]">
            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
              {info.project.rows.map((r) => (
                <div key={r.key} className="contents">
                  <span className="text-[var(--color-text-muted)]">
                    {r.key}
                  </span>
                  <span className="break-words text-[var(--color-text)]">
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                if (activeModelId && info.project) {
                  void viewerApi.selectByLocalId(
                    activeModelId,
                    info.project.localId,
                  )
                }
              }}
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              title="Seleciona o IfcProject para ver todas as propriedades, Psets e relações no painel Propriedades"
            >
              Abrir no painel Propriedades
            </button>
          </div>
        )}
      </Section>

      <Section title="Origens da hierarquia espacial" defaultOpen={false}>
        <div className="space-y-2 text-[11px]">
          <p className="text-[10px] leading-snug text-[var(--color-text-muted)]">
            Origens EXATAS dos IfcLocalPlacement de Site / Building /
            Storey / Space, lidas diretamente do ficheiro (parser paralelo
            web-ifc) e convertidas para metros, sem o recentramento do
            conversor. Coordenadas no referencial do ficheiro (E, N, h).
          </p>
          <div className="flex items-center gap-1 rounded border border-[var(--color-border)]/50 bg-[var(--color-bg)]/40 px-1.5 py-1">
            <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-[var(--color-text)]">
              Origem do ficheiro IFC (0, 0, 0)
            </span>
            <BotaoIrParaOrigem
              activo={origemEmFoco?.modelId === activeModelId && origemEmFoco?.chave === 'ficheiro'}
              onIr={() =>
                void irParaOrigem(
                  activeModelId,
                  'ficheiro',
                  `Origem do ficheiro IFC · ${activeMeta?.fileName ?? activeModelId}`,
                )
              }
            />
          </div>
          {origemEmFoco && (
            <button
              type="button"
              onClick={() => setOriginMarkerFocus(null)}
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              title="Volta a mostrar no 3D todas as origens que o filtro de origens deixa ver."
            >
              Mostrar todas as origens
            </button>
          )}
          {!origins && (
            <button
              type="button"
              onClick={onComputeOrigins}
              disabled={originsBusy}
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
              title="Carrega o parser IFC paralelo para este modelo (usa memória; os bytes já estão em cache) e extrai as origens exatas. Também ativa os pontos ciano no marcador de origem 3D (Aparência > Referências > Origem)."
            >
              {originsBusy ? 'A extrair…' : 'Calcular origens exatas'}
            </button>
          )}
          {originsErr && (
            <div className="text-[10px] text-[var(--color-error-text)]">{originsErr}</div>
          )}
          {origins && (
            <>
              <div className="text-[10px] text-[var(--color-text-dim)]">
                Unidade do ficheiro: {origins.unitLabel} (fator para metros:{' '}
                {origins.factorToMetres}). Cada nó indica{' '}
                <strong>a que referencial</strong> estão as suas coordenadas.
              </div>
              <div className="max-h-72 space-y-1 overflow-y-auto pr-0.5">
                {origins.items.map((n) => (
                  <div
                    key={n.expressID}
                    className="rounded border border-[var(--color-border)]/50 bg-[var(--color-bg)]/40 px-1.5 py-1"
                    style={{ marginLeft: `${n.level * 8}px` }}
                  >
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (activeModelId)
                            void viewerApi.selectByLocalId(
                              activeModelId,
                              n.expressID,
                            )
                        }}
                        className="min-w-0 flex-1 truncate text-left text-[11px] font-medium text-[var(--color-text)] hover:text-[var(--color-accent)]"
                        title="Clica para selecionar este nó no 3D."
                      >
                        {spatialTypeLabel(n.ifcType)}
                        {n.name ? ` · ${n.name}` : ''}
                      </button>
                      <BotaoIrParaOrigem
                        activo={origemEmFoco?.modelId === activeModelId && origemEmFoco?.chave === n.expressID}
                        onIr={() =>
                          void irParaOrigem(
                            activeModelId,
                            n.expressID,
                            `${spatialTypeLabel(n.ifcType)}${n.name ? ' · ' + n.name : ''}`,
                          )
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setEditNode((cur) =>
                            cur?.expressID === n.expressID ? null : n,
                          )
                        }
                        className={
                          'shrink-0 rounded border px-1 py-0.5 text-[9px] ' +
                          (editNode?.expressID === n.expressID
                            ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                            : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:text-[var(--color-text)]')
                        }
                        title="Editar a origem deste placement no próprio ficheiro IFC (descarrega uma cópia; o original não é alterado)."
                      >
                        editar
                      </button>
                    </div>
                    <div className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-1.5 text-[10px] leading-snug">
                      <span className="text-[var(--color-text-dim)]">
                        referencial
                      </span>
                      <span className="truncate text-[var(--color-text-muted)]">
                        {n.parentLabel}
                      </span>
                      <span className="text-[var(--color-text-dim)]">
                        coordenadas
                      </span>
                      <span className="font-mono text-[var(--color-text)]">
                        {n.relative
                          ? `E ${formatCoord(n.relative[0], 2)} · N ${formatCoord(n.relative[1], 2)} · h ${formatCoord(n.relative[2], 2)}`
                          : '(sem Location)'}
                      </span>
                      {(Math.abs(n.origin[0] - (n.relative?.[0] ?? 0)) >
                        0.005 ||
                        Math.abs(n.origin[1] - (n.relative?.[1] ?? 0)) >
                          0.005 ||
                        Math.abs(n.origin[2] - (n.relative?.[2] ?? 0)) >
                          0.005) && (
                        <>
                          <span className="text-[var(--color-text-dim)]">
                            abs. ficheiro
                          </span>
                          <span className="font-mono text-[var(--color-text-muted)]">
                            E {formatCoord(n.origin[0], 2)} · N{' '}
                            {formatCoord(n.origin[1], 2)} · h{' '}
                            {formatCoord(n.origin[2], 2)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {origins.items.length === 0 && (
                  <div className="italic text-[var(--color-text-dim)]">
                    Sem nós espaciais com ObjectPlacement.
                  </div>
                )}
              </div>
              {editNode && activeModelId && activeMeta && (
                <PlacementEditForm
                  key={`${activeModelId}:${editNode.expressID}`}
                  modelId={activeModelId}
                  fileName={activeMeta.fileName}
                  node={editNode}
                  onClose={() => setEditNode(null)}
                />
              )}
              <div className="text-[9px] leading-snug text-[var(--color-text-dim)]">
                Representação 3D destes nós é opcional: liga o toggle Origem
                (Vista &gt; Cena) e escolhe as categorias no filtro ao lado
                (por defeito só a origem do ficheiro é desenhada).
              </div>
            </>
          )}
        </div>
      </Section>

      <Section title="Propriedades presentes" defaultOpen={false}>
        <div className="space-y-2 text-[11px]">
          <p className="text-[10px] leading-snug text-[var(--color-text-muted)]">
            Que Property Sets e propriedades existem no modelo, e em
            quantos elementos (amostra dos elementos com geometria).
            Clica numa propriedade para abrir o Filtro (Shift+F) já
            apontado a ela.
          </p>
          {!dict && (
            <button
              type="button"
              onClick={onComputeDictionary}
              disabled={dictBusy}
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
              title="Lê as propriedades de todos os elementos com geometria (até 10 000; a primeira vez pode custar segundos e memória em modelos grandes)."
            >
              {dictBusy ? 'A ler propriedades…' : 'Calcular dicionário'}
            </button>
          )}
          {dictErr && <div className="text-[10px] text-[var(--color-error-text)]">{dictErr}</div>}
          {dict && (
            <>
              <div className="text-[10px] text-[var(--color-text-dim)]">
                {dict.psets.length} Property Sets em {dict.sampled} elementos
                amostrados
                {dict.truncated && ' (modelo truncado no limite de 10 000)'}.
              </div>
              {dict.psets.length === 0 ? (
                <div className="italic text-[var(--color-text-muted)]">
                  Nenhum IfcPropertySet nos elementos amostrados.
                </div>
              ) : (
                <div className="max-h-72 space-y-1.5 overflow-y-auto pr-0.5">
                  {dict.psets.map((ps) => (
                    <div
                      key={ps.name}
                      className="rounded border border-[var(--color-border)]/50 bg-[var(--color-bg)]/40 px-1.5 py-1"
                    >
                      <div className="mb-0.5 font-mono text-[10px] font-medium text-[var(--color-text)]">
                        {ps.name}
                      </div>
                      <ul className="flex flex-col gap-px">
                        {ps.props.map((p) => (
                          <li key={p.name}>
                            <button
                              type="button"
                              onClick={() => openFilterForProp(ps.name, p.name)}
                              className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-[var(--color-bg)]"
                              title={`Abrir o Filtro apontado a ${ps.name}.${p.name}`}
                            >
                              <span className="min-w-0 flex-1 truncate text-[var(--color-text-muted)] hover:text-[var(--color-accent)]">
                                {p.name}
                              </span>
                              <span className="shrink-0 font-mono text-[9px] text-[var(--color-text-dim)]">
                                {p.count}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Section>

      {activeModelId && (
        <Section
          title="Verificação da georreferência"
          defaultOpen={verificacoesGuardadas(activeModelId)?.verificacoes.some((v) => v.gravidade === 'erro') ?? false}
        >
          <VerificacoesGeorrefView modelId={activeModelId} />
        </Section>
      )}

      <Section title="CRS / Origem" defaultOpen={distanciaGrande !== undefined}>
        {activeModelId && distanciaGrande !== undefined && (
          <p
            className="mb-2 rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-2 py-1 text-[11px] leading-snug text-[var(--color-warn-text)]"
            data-campo="aviso-coordenadas-grandes"
          >
            {avisoDeCoordenadasGrandes('Coordenadas grandes', distanciaGrande)}
          </p>
        )}
        {!crs && activeModelId && leituraNova ? (
          // O Fragments não viu CRS, mas o texto tem georreferenciação
          // (ePset_MapConversion do IFC2X3, ou IfcMapConversionScaled).
          <LeituraDoMapaView modelId={activeModelId} comCabecalho />
        ) : !crs && activeModelId && sitioGuardado(activeModelId) ? (
          <SitioView modelId={activeModelId} />
        ) : !crs ? (
          <div className="text-[11px] italic text-[var(--color-text-muted)]">
            O modelo não declara IfcMapConversion, ePset_MapConversion (IFC2X3)
            nem latitude e longitude no IfcSite: não há nada que o ponha no mapa.
            A posição pode definir-se à mão no painel Posição.
          </div>
        ) : (
          <div className="space-y-2 text-[11px]">
            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
              <span className="text-[var(--color-text-muted)]">Nome</span>
              <span className="font-mono text-[var(--color-text)]">
                {crs.name ?? '-'}
              </span>
              <span className="text-[var(--color-text-muted)]">Descrição</span>
              <span className="text-[var(--color-text)]">
                {crs.description ?? '-'}
              </span>
              <span className="text-[var(--color-text-muted)]">Datum</span>
              <span className="text-[var(--color-text)]">
                {crs.geodeticDatum ?? '-'}
              </span>
              <span className="text-[var(--color-text-muted)]">Vertical</span>
              <span className="text-[var(--color-text)]">
                {crs.verticalDatum ?? '-'}
              </span>
              <span className="text-[var(--color-text-muted)]">Projecção</span>
              <span className="text-[var(--color-text)]">
                {crs.mapProjection ?? '-'}
              </span>
              <span className="text-[var(--color-text-muted)]">Zona</span>
              <span className="text-[var(--color-text)]">
                {crs.mapZone ?? '-'}
              </span>
              <span className="text-[var(--color-text-muted)]">Unidade</span>
              <span className="text-[var(--color-text)]">
                {crs.mapUnit ?? '-'}
              </span>
            </div>

            {mc ? (
              <div className="rounded border border-emerald-700/50 bg-[var(--color-ok-bg)] px-2 py-1">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-400/90">
                    IfcMapConversion · APLICADO
                  </div>
                  <span
                    className="text-[10px] text-emerald-400/80"
                    title="A transformação MapConversion foi aplicada como matriz de mundo no modelo. Eastings/Northings/Altura tornam-se a origem efectiva do modelo na cena."
                  >
                    geo
                  </span>
                </div>
                <div className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                  <span className="text-[var(--color-text-muted)]">
                    Eastings
                  </span>
                  <span className="font-mono text-[var(--color-text)]">
                    {formatCoord(mc.eastings)}
                  </span>
                  <span className="text-[var(--color-text-muted)]">
                    Northings
                  </span>
                  <span className="font-mono text-[var(--color-text)]">
                    {formatCoord(mc.northings)}
                  </span>
                  <span className="text-[var(--color-text-muted)]">
                    Altura ortogonal
                  </span>
                  <span className="font-mono text-[var(--color-text)]">
                    {formatCoord(mc.orthogonalHeight)}
                  </span>
                  <span className="text-[var(--color-text-muted)]">
                    Rotação X
                  </span>
                  <span className="font-mono text-[var(--color-text)]">
                    cos={formatCoord(mc.xAxisAbscissa, 6)}, sin=
                    {formatCoord(mc.xAxisOrdinate, 6)} (
                    {formatCoord(
                      (Math.atan2(mc.xAxisOrdinate, mc.xAxisAbscissa) * 180) /
                        Math.PI,
                      4,
                    )}
                    °)
                  </span>
                  <span className="text-[var(--color-text-muted)]">
                    Escala
                  </span>
                  <span className="font-mono text-[var(--color-text)]">
                    {mc.scale}
                  </span>
                </div>
                {activeModelId && <LeituraDoMapaView modelId={activeModelId} />}
                {escalaNaoAplicada !== null && (
                  <div className="mt-2 rounded border border-[var(--color-border)] px-2 py-1.5 text-[11px] text-[var(--color-text-muted)]">
                    Num ficheiro .frag não se conhece a unidade de comprimento
                    do projecto, que o Scale do IfcMapConversion pressupõe. O
                    Scale declarado ({mc.scale}) não foi aplicado: o modelo
                    fica à escala 1, com a posição e a rotação declaradas.
                    Para a georreferenciação completa, abrir o IFC original.
                  </div>
                )}
                {scaleCheck?.suspicious && (
                  <div className="mt-2 rounded border border-amber-700/50 bg-[var(--color-warn-bg)] px-2 py-1.5 text-[11px] text-[var(--color-warn-text)]">
                    <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-accent)]">
                      Escala incompatível com a unidade do ficheiro ⚠
                    </div>
                    O Scale declarado ({mc.scale}) com a unidade do projeto (
                    {origins?.unitLabel ?? '?'}) dá uma escala geométrica de{' '}
                    <strong>{formatCoord(scaleCheck.geometricScale, 3)}×</strong>
                    . O modelo desenha bem, mas o mundo do viewer fica nessa
                    unidade em vez de metros: a posição no mapa e o export
                    Google Earth (KMZ) assumem metros e saem no sítio errado e
                    com o tamanho errado. Para um ficheiro nesta unidade o Scale
                    devia ser <strong>{scaleCheck.expectedFileScale}</strong>.
                    Corrige-se no exportador que gerou o IFC.
                  </div>
                )}
              </div>
            ) : (
              <div className="italic text-[var(--color-text-muted)]">
                Sem MapConversion. Não há transformação local→global declarada.
              </div>
            )}

            {info?.coordinates && info.coordinates.length > 0 && (
              <div>
                <span className="text-[var(--color-text-muted)]">
                  WorldCoordinateSystem origin (modelo):
                </span>{' '}
                <code className="font-mono text-[var(--color-text)]">
                  [{info.coordinates.map((n) => formatCoord(n)).join(', ')}]
                </code>
              </div>
            )}
          </div>
        )}
        {trueNorthDeg !== null && (
          <div
            className="mt-2 rounded border border-[var(--color-border)]/50 bg-[var(--color-bg)]/40 px-2 py-1 text-[11px]"
            title="Direção do norte geográfico declarada no IfcGeometricRepresentationContext (TrueNorth), medida do +Y do projeto para o norte verdadeiro, sentido horário. Meramente declarativa por spec: o viewer NÃO roda a geometria com ela."
          >
            <span className="text-[var(--color-text-muted)]">
              TrueNorth declarado:
            </span>{' '}
            <span className="font-mono text-[var(--color-text)]">
              {formatCoord(trueNorthDeg, 3)}°
            </span>{' '}
            <span className="text-[10px] text-[var(--color-text-dim)]">
              {activeModelId && sitioGuardado(activeModelId)?.aplicado
                ? '(aplicado na colocação pelo IfcSite)'
                : '(informativo, não aplicado à geometria)'}
            </span>
          </div>
        )}
      </Section>
        </>
      )}
    </div>
  )
}
// Formulário de edição da origem de um placement espacial (Site /
// Building / Storey / Space) com write-back ao próprio IFC. Os campos
// editam a origem RELATIVA AO PAI (em metros); "manter posições
// absolutas" compensa os placements filhos por -Δ, o que na prática
// re-referencia a hierarquia a este nó (ex.: passar as coordenadas do
// terreno para o IfcSite e zerar o resto).
function PlacementEditForm({
  modelId,
  fileName,
  node,
  onClose,
}: {
  modelId: string
  fileName: string
  node: SpatialOrigin
  onClose: () => void
}) {
  const initial = node.relative ?? node.origin
  const [vals, setVals] = useState<[string, string, string]>([
    String(initial[0]),
    String(initial[1]),
    String(initial[2]),
  ])
  const [compensate, setCompensate] = useState(true)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null,
  )

  const onSave = () => {
    setMsg(null)
    const nums = vals.map((v) => Number(v.trim().replace(',', '.')))
    if (nums.some((n) => !Number.isFinite(n))) {
      setMsg({ kind: 'err', text: 'Valores E/N/h têm de ser numéricos.' })
      return
    }
    const bytes = rawIfc.getCachedBytes(modelId)
    if (!bytes) {
      setMsg({
        kind: 'err',
        text: 'Bytes do IFC não estão em cache; recarrega o modelo.',
      })
      return
    }
    try {
      const res = writeProductPlacement(bytes, {
        productExpressID: node.expressID,
        newLocationMetres: [nums[0], nums[1], nums[2]],
        compensateChildren: compensate,
      })
      const outName = `${fileName.replace(/\.ifc$/i, '')}_placement.ifc`
      downloadBlob(res.bytes.slice().buffer, outName)
      useAppStore.getState().pushToast(`Origem gravada em ${outName}`, {
        type: 'success',
      })
      setMsg({
        kind: 'ok',
        text:
          `_placement.ifc descarregado. Origem ${spatialTypeLabel(node.ifcType)} ` +
          `movida de [${res.oldLocationFileUnits.map((v) => formatCoord(v, 3)).join(', ')}] para ` +
          `[${res.newLocationFileUnits.map((v) => formatCoord(v, 3)).join(', ')}] (unidades do ficheiro)` +
          (compensate
            ? `; ${res.compensatedChildren} placement${res.compensatedChildren === 1 ? '' : 's'} filho${res.compensatedChildren === 1 ? '' : 's'} compensado${res.compensatedChildren === 1 ? '' : 's'}.`
            : '; filhos NÃO compensados (tudo o que está abaixo moveu-se com o nó).'),
      })
    } catch (err) {
      setMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Falha a escrever o IFC.',
      })
    }
  }

  return (
    <div className="rounded border border-[var(--color-accent-border)] bg-[var(--color-bg)]/60 px-2 py-1.5">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] font-medium text-[var(--color-text)]">
          Editar origem: {spatialTypeLabel(node.ifcType)}
          {node.name ? ` · ${node.name}` : ''}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded px-1 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          title="Fechar sem gravar"
        >
          ✕
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {(['E', 'N', 'h'] as const).map((lbl, i) => (
          <label
            key={lbl}
            className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)]"
          >
            {lbl}
            <input
              type="text"
              inputMode="decimal"
              value={vals[i]}
              onChange={(e) =>
                setVals((v) => {
                  const next = [...v] as [string, string, string]
                  next[i] = e.target.value
                  return next
                })
              }
              className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 font-mono text-[10px] text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none"
            />
          </label>
        ))}
      </div>
      <div className="mt-1 text-[9px] text-[var(--color-text-dim)]">
        Metros, no referencial do pai do placement (para o Site, o
        referencial do ficheiro).
      </div>
      <label
        className="mt-1 flex cursor-pointer items-center gap-1.5 text-[10px] text-[var(--color-text-muted)]"
        title="Com isto ligado, os placements diretamente abaixo deste nó são ajustados por -Δ e NADA muda de posição absoluta: só a repartição das coordenadas pela hierarquia (ex.: mover as coordenadas do terreno para o IfcSite). Desligado, o nó e tudo o que está abaixo movem-se mesmo."
      >
        <input
          type="checkbox"
          checked={compensate}
          onChange={(e) => setCompensate(e.target.checked)}
        />
        Manter posições absolutas (compensar filhos)
      </label>
      <button
        type="button"
        onClick={onSave}
        className="mt-1.5 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-accent)] hover:brightness-110"
        title="Escreve a nova origem nos bytes do IFC (entidades novas; pontos partilhados nunca são reescritos) e descarrega <nome>_placement.ifc. O original não é alterado."
      >
        Gravar no IFC (descarregar)
      </button>
      {msg && (
        <div
          className={
            'mt-1 text-[10px] leading-tight ' +
            (msg.kind === 'ok' ? 'text-[var(--color-ok-text)]' : 'text-[var(--color-error-text)]')
          }
        >
          {msg.text}
        </div>
      )}
    </div>
  )
}

/** "Ir": leva a câmara à origem e deixa-a isolada no 3D, com as
 *  coordenadas na etiqueta. Aceso quando é essa a origem em foco. */
function BotaoIrParaOrigem({ activo, onIr }: { activo: boolean; onIr: () => void }) {
  return (
    <button
      type="button"
      onClick={onIr}
      className={
        'shrink-0 rounded border px-1 py-0.5 text-[9px] ' +
        (activo
          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
          : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:text-[var(--color-text)]')
      }
      title="Leva a câmara a esta origem e mostra só esta no 3D, com as coordenadas no ficheiro e no CRS. Para voltar a ver todas: Mostrar todas as origens."
    >
      ir
    </button>
  )
}

export default ModelInfoPanel
